import React, { useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTenant } from "../../TenantContext";
import useSiteTitle from "../../hooks/useSiteTitle";
import "./Style.css";
import { useConfig } from "../../ConfigContext";
import { buildLegalLinks } from "../Legal/legalLinks";
import LegalLink from "../Legal/LegalLink";

// `key` is the CMS field (config.<key>, set in Admin > App Info); `href` is
// only the fallback used when that field is empty.
const PLATFORMS = [
  { src: "/RokuLogo.png", alt: "Roku", key: "roku_app_url" },
  { src: "/GooglePlayStore.png", alt: "Google Play", key: "android_app_url" },
  { src: "/AppleStore.png", alt: "App Store", key: "ios_app_url" },
  { src: "/Samsung.png", alt: "Samsung", key: "samsung_tv_app_url" },
  { src: "/LG.png", alt: "LG", key: "lg_tv_app_url" },
  { src: "/FireTv.png", alt: "Fire TV", key: "firetv_app_url" },
];

// Keeps the footer at the bottom of the screen on pages shorter than the
// viewport, whatever the page's markup: pages render <Footer /> at different
// nesting depths, so a flex/sticky-footer layout can't reach them all. It
// adds exactly the missing space as top margin (0 on long pages).
function useStickToBottom(ref) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const current = parseFloat(el.style.marginTop) || 0;
        // Where the footer would end without the extra margin.
        const naturalBottom = el.getBoundingClientRect().bottom + window.scrollY - current;
        const gap = Math.max(0, Math.floor(window.innerHeight - naturalBottom));
        if (gap !== current) el.style.marginTop = `${gap}px`;
      });
    };
    update();
    // Content above loads in asynchronously (rails, images), so re-measure
    // whenever the page's size changes, not just on window resize.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(document.body);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [ref]);
}

const Footer = () => {
  const location = useLocation();
  const footerRef = useRef(null);
  useStickToBottom(footerRef);
  // Tenant info is resolved from the tenant-info API on load (TenantContext).
  // Prefer it for branding; fall back to env so single-tenant/legacy is unchanged.
  const { tenant, logo } = useTenant();
  // Branding Site Title (v1/config) first, then tenant name, then env.
  const siteTitle = useSiteTitle();
  // Support email is per tenant (Tenant.contactEmail, set in SuperAdmin).
  const contactEmail = tenant?.contactEmail;
  // Unified logo chain (tenant logo -> env logo -> bundled default) from TenantContext.
  const brandLogo = logo;

  const config = useConfig();
  // From the CMS (config.legal_links); env vars only as a fallback.
  const legalLinks = buildLegalLinks(config);
  // Each tenant's own store links, from the CMS. Deliberately no hardcoded
  // default: a tenant with no URL set renders a plain, unlinked badge rather
  // than pointing its viewers at another tenant's app.
  const platforms = PLATFORMS.map((p) => ({ ...p, href: config?.[p.key] || null }));
  // Admin > Settings > Site identity; `{year}` becomes the current year.
  const year = new Date().getFullYear();
  const copyright = config?.copyright_text
    ? config.copyright_text.replace(/\{year\}/g, year)
    : `© ${year} ${siteTitle}. All rights reserved.`;

  return (
    <footer className="ott-footer" ref={footerRef}>
      <div className="ott-footer__inner">

        {/* ── Top: brand + link columns ── */}
        <div className="ott-footer__top">

          {/* Brand */}
          <div className="ott-footer__brand">
            <img
              src={brandLogo}
              alt={siteTitle}
              className="ott-footer__logo"
            />
            <p className="ott-footer__tagline">
              Stream movies, series &amp; live TV — anytime, on any screen.
            </p>
          </div>

          {/* Help */}
          <div className="ott-footer__col">
            <h4 className="ott-footer__heading">Help</h4>
            <Link className="ott-footer__link" to="/help-center">
              Help Center &amp; FAQ
            </Link>
            {/* Tell the form which page the viewer was on. */}
            <Link
              className="ott-footer__link"
              to="/support"
              state={location.pathname === "/support" ? undefined : { from: location.pathname }}
            >
              Contact Support
            </Link>
            {/* Off per tenant in Admin > Settings; still shown while config loads. */}
            {config?.enable_contact_us !== false && (
              <Link className="ott-footer__link" to={process.env.REACT_APP_CONTACT_URL || "/contact-us"}>
                Contact Us
              </Link>
            )}
          </div>

          {/* Legal */}
          {legalLinks.length > 0 && (
            <div className="ott-footer__col">
              <h4 className="ott-footer__heading">Legal</h4>
              {legalLinks.map((l) => (
                <LegalLink key={l.type} link={l} className="ott-footer__link" />
              ))}
            </div>
          )}

          {/* Platforms */}
          <div className="ott-footer__col ott-footer__col--platforms">
            <h4 className="ott-footer__heading">Available on</h4>
            <div className="ott-footer__platforms">
              {platforms.map((p) =>
                p.href ? (
                  <a
                    className="ott-footer__platform"
                    key={p.alt}
                    title={p.alt}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={p.src} alt={p.alt} loading="lazy" />
                  </a>
                ) : (
                  <span className="ott-footer__platform" key={p.alt} title={p.alt}>
                    <img src={p.src} alt={p.alt} loading="lazy" />
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        {/* ── Contact + Bottom bar ── */}
        {contactEmail && (
          <div className="footer-contact">
            Contact Us -{" "}
            <a className="ott-footer__link" href={`mailto:${contactEmail}`}>
              {contactEmail}
            </a>
          </div>
        )}

        <div className="ott-footer__bottom">
          <span className="ott-footer__copy">{copyright}</span>
          <span className="ott-footer__made">Crafted for cinematic streaming.</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;